import { WelcomePage } from "@src/pages/WelcomePage";
import { canAll, canAny } from "@src/security/permissions";

import { displayPersonList } from "@src/person/PersonList";
import {
  displayReadPerson,
  displayEditPerson,
} from "@src/forms/PersonForm";

import { displayVoterApplicationList } from "@src/lists/VoterApplicationList";
import { displayUserAccountRequestList } from "@src/lists/UserAccountRequestList";
import { displayVoterList } from "@src/lists/VoterList";
import { displayVotingCenterList } from "@src/lists/VotingCenterList";
import { displayReadUserAccountRequest } from "@src/forms/UserAccountRequestForm";

import {
  displayReadVoterApplication,
  displayEditVoterApplication,
} from "@src/forms/VoterApplicationForm";

import {
  displayReadVoter,
  displayEditVoter,
} from "@src/forms/VoterForm";

import {
  displayReadUserAccount,
  displayEditUserAccount,
} from "@src/forms/UserAccountForm";

import {
  displayDiagnosticsList,
  displayDiagnosticsDetails
} from "@src/diagnostics";

import { displayElectoralGeography } from "@src/pages/ElectoralGeographyPage";

import { displayUserAccountList } from "@src/lists/UserAccountList";

import { displayReportingOverview } from "@src/pages/ReportingOverviewPage";
import { displayAdvancedReporting } from "@src/pages/ReportingPage";

import { displayQalaaUserList } from "@auto/users/__QalaaUserList";
import {
  displayQalaaUserProfile
} from "@auto/users/__QalaaUserProfile";

import { displayOperatorReport } from "@src/reporting/pages/OperatorReport.jsx";

import { displayTabletReports } from "@src/reporting/pages/TabletReports";

import { displayVotingCenterReports } from "@src/reporting/pages/VotingCenterReportsList";
import { displayVotingCenterReport } from "@src/reporting/pages/VotingCenterReport";

import {
  displayPreliminaryRolls,
  displayFinalRolls
} from "@src/voter-roll/VoterRollList";

import {
  displayPreliminaryRoll,
  displayFinalRoll
} from "@src/voter-roll/VoterRollDetails";

import {
  displayVoterRollPreview
} from "@src/voter-roll/VoterRollPreview";


import {
  displayApiUserList
} from "@auto/users/__ApiUserList";

import {
  displayReadApiUser
} from "@auto/users/__ApiUserForm";

import { displayApiDocumentation } from "@auto/doc/ApiDocumentationPage";

const accessDenied = () => ({
  uuid: "access-denied",
  view: () => (
    <div style={{ padding: 24 }}>
      <h1>403</h1>
      <div>Access denied</div>
    </div>
  )
});

const requireAll = (permissions, view) => (gui, params) => {
  if (!canAll(gui, permissions)) {
    return accessDenied();
  }

  return view(gui, params);
};

const requireAny = (permissions, view) => (gui, params) => {
  if (!canAny(gui, permissions)) {
    return accessDenied();
  }

  return view(gui, params);
};

export const routes = [
  {
    path: "/",
    view: (gui) => ({
      view: () => <WelcomePage gui={gui} />,
    }),
  },

  {
    path: "/electoral-geography",
    view: requireAny([
      "CREATE_DEPARTMENT", "UPDATE_DEPARTMENT",
      "CREATE_ARRONDISSEMENT", "UPDATE_ARRONDISSEMENT",
      "CREATE_COMMUNE", "UPDATE_COMMUNE",
      "CREATE_SECTION", "UPDATE_SECTION",
      "CREATE_CONSTITUENCY", "UPDATE_CONSTITUENCY",
      "CREATE_VOTING_CENTER", "UPDATE_VOTING_CENTER"
    ], (gui) => displayElectoralGeography(gui)),
  },

  // ---- PERSON REGISTRY ----
  {
    path: "/person/new",
    view: requireAll(["CREATE_PERSON"], (gui) => displayEditPerson(gui, null)),
  },
  {
    path: "/person/:id/edit",
    view: requireAll(["READ_PERSON", "UPDATE_PERSON"], (gui, params) => displayEditPerson(gui, params.id)),
  },
  {
    path: "/person/:id",
    view: requireAll(["READ_PERSON"], (gui, params) => displayReadPerson(gui, params.id)),
  },
  {
    path: "/person",
    view: requireAll(["READ_PERSON"], (gui) => displayPersonList(gui)),
  },

  // ---- VOTER APPLICATIONS ----
  {
    path: "/voter-application/new",
    view: requireAll(["CREATE_VOTER_APPLICATION"], (gui) => displayEditVoterApplication(gui, null)),
  },
  {
    path: "/voter-application/:id/edit",
    view: requireAll(["READ_VOTER_APPLICATION", "UPDATE_VOTER_APPLICATION"], (gui, params) => displayEditVoterApplication(gui, params.id)),
  },
  {
    path: "/voter-application/:id",
    view: requireAll(["READ_VOTER_APPLICATION"], (gui, params) => displayReadVoterApplication(gui, params.id)),
  },
  {
    path: "/voter-application",
    view: requireAll(["SEARCH_VOTER_APPLICATION", "COUNT_VOTER_APPLICATION"], (gui) => displayVoterApplicationList(gui)),
  },

  // ---- VOTERS ----
  {
    path: "/voter/new",
    view: requireAll(["CREATE_VOTER"], (gui) => displayEditVoter(gui, null)),
  },
  {
    path: "/voter/:id/edit",
    view: requireAll(["READ_VOTER", "UPDATE_VOTER"], (gui, params) => displayEditVoter(gui, params.id)),
  },
  {
    path: "/voter/:id",
    view: requireAll(["READ_VOTER"], (gui, params) => displayReadVoter(gui, params.id)),
  },
  {
    path: "/voter",
    view: requireAll(["SEARCH_VOTER", "COUNT_VOTER"], (gui) => displayVoterList(gui)),
  },

  // ---- USER ACCOUNT REQUESTS ----
  {
    path: "/user-account-request/:gid",
    view: requireAll(["READ_USER_ACCOUNT_REQUEST"], (gui, params) => displayReadUserAccountRequest(gui, params.gid)),
  },
  {
    path: "/user-account-request",
    view: requireAll(["READ_USER_ACCOUNT_REQUEST"], (gui) => displayUserAccountRequestList(gui)),
  },

  // ---- USER ACCOUNTS ----
  {
    path: "/user-account/new",
    view: requireAll(["CREATE_USER_ACCOUNT"], (gui) => displayEditUserAccount(gui, null)),
  },
  {
    path: "/user-account/:id/edit",
    view: requireAll(["READ_USER_ACCOUNT", "UPDATE_USER_ACCOUNT"], (gui, params) => displayEditUserAccount(gui, params.id)),
  },
  {
    path: "/user-account/:id",
    view: requireAll(["READ_USER_ACCOUNT"], (gui, params) => displayReadUserAccount(gui, params.id)),
  },
  {
    path: "/user-account",
    view: requireAll(["READ_USER_ACCOUNT"], (gui) => displayUserAccountList(gui)),
  },

  // ---- ELECTORAL GEOGRAPHY: voting-center* ----
  {
    path: "/voting-center",
    view: requireAll(["COUNT_VOTING_CENTER"], (gui) => displayVotingCenterList(gui)),
  },

  // ---- VOTER ROLLS ----
  {
    path: "/voter-roll/preliminary/:id",
    view: requireAll(["READ_VOTER_ROLL"], (gui, params) => displayPreliminaryRoll(gui, params.id)),
  },
  {
    path: "/voter-roll/preliminary",
    view: requireAll(["SEARCH_VOTER_ROLL", "COUNT_VOTER_ROLL"], (gui) => displayPreliminaryRolls(gui)),
  },
  {
    path: "/voter-roll/final/:id",
    view: requireAll(["READ_VOTER_ROLL"], (gui, params) => displayFinalRoll(gui, params.id)),
  },
  {
    path: "/voter-roll/final",
    view: requireAll(["SEARCH_VOTER_ROLL", "COUNT_VOTER_ROLL"], (gui) => displayFinalRolls(gui)),
  },

  // ---- DIAGNOSTICS ----
  {
    path: "/diagnostics",
    view: requireAll(["READ_DIAGNOSTICS", "COUNT_DIAGNOSTICS"], (gui) => displayDiagnosticsList(gui)),
  },
  {
    path: "/diagnostics/:id",
    view: requireAll(["READ_DIAGNOSTICS"], (gui, params) => displayDiagnosticsDetails(gui, params.id)),
  },

  {
    path: "/reporting",
    view: requireAll(["READ_STATISTICAL_REPORTS"], (gui) => displayReportingOverview(gui)),
  },
  {
    path: "/reporting/advanced",
    view: requireAll(["READ_STATISTICAL_REPORTS"], (gui) => displayAdvancedReporting(gui)),
  },
  {
    path: "/reporting/operators/:ninu",
    view: requireAll(
      ["READ_OPERATIONAL_REPORTS"],
      (gui, params) => displayOperatorReport(gui, params.ninu)
    ),
  },
  {
    path: "/reporting/tablets",
    view: requireAll(["READ_OPERATIONAL_REPORTS"], (gui) => displayTabletReports(gui)),
  },

  {
    path: "/reporting/voting-centers/:gid/voter-roll-preview",
    view: requireAll(
      [
        "READ_OPERATIONAL_REPORTS",
        "SEARCH_VOTER",
        "COUNT_VOTER",
        "READ_PERSON"
      ],
      (gui, params) => displayVoterRollPreview(gui, params.gid)
    ),
  },
  {
    path: "/reporting/voting-centers/:gid",
    view: requireAll(
      ["READ_OPERATIONAL_REPORTS"],
      (gui, params) => displayVotingCenterReport(gui, params.gid)
    ),
  },
  {
    path: "/reporting/voting-centers",
    view: requireAll(
      ["READ_OPERATIONAL_REPORTS"],
      (gui) => displayVotingCenterReports(gui)
    ),
  },

  // ---- QALAA USERS ----
  {
    path: "/__qalaa-user/:id",
    view: (gui, params) =>
      requireAll(["READ_QALAA_USER"], (gui, params) => displayQalaaUserProfile(gui, params.id))(gui, params),
  },
  {
    path: "/__qalaa-user",
    view: (gui) =>
      requireAll(["SEARCH_QALAA_USER", "COUNT_QALAA_USER"], (gui) => displayQalaaUserList(gui))(gui),
  },
    // ---- API USERS ----
  {
    path: "/__api-users/:id",
    view: (gui, params) =>
      requireAll(["READ_API_USERS"], (gui, params) => displayReadApiUser(gui, params.id))(gui, params),
  },
  {
    path: "/__api-users",
    view: (gui) =>
      requireAll(["SEARCH_API_USERS", "COUNT_API_USERS"], (gui) => displayApiUserList(gui))(gui),
  },
  // ---- DEVELOPER ----
  {
    path: "/developer/api",
    view: requireAll(
      ["READ_API_DOCUMENTATION"],
      (gui) => displayApiDocumentation(gui)
    ),
  },

];

export default routes;
